import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase/config';
import { collection, query, where, onSnapshot, doc, writeBatch, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { EyeIcon, CheckCircleIcon, XCircleIcon, ExclamationIcon } from '../../components/icons/Icons';
import { sendNotificationEmail, leaveStatusUpdateTemplate } from '../../firebase/notifications';

// --- Helper Modals ---

function LeaveDetailModal({ request, onClose }) {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-lg">
                <h2 className="text-xl font-bold text-[#213368] mb-4">Leave Request Details</h2>
                <div className="space-y-3 text-sm">
                    <p><strong>Requester:</strong> {request.requesterName}</p>
                    <p><strong>Department:</strong> {request.department}</p>
                    <p><strong>Leave Type:</strong> {request.leaveType}</p>
                    <p><strong>Dates:</strong> {request.startDate.toDate().toLocaleDateString()} to {request.endDate.toDate().toLocaleDateString()} ({request.duration} days)</p>
                    <div>
                        <p><strong>Reason:</strong></p>
                        <p className="p-2 border rounded-md bg-gray-50">{request.reason}</p>
                    </div>
                    {/* This correctly shows violations for items from both tabs */}
                    {request.policyViolations && request.policyViolations.length > 0 && (
                        <div className="bg-yellow-50 border-l-4 border-yellow-400 text-yellow-700 p-3">
                            <p className="font-bold flex items-center"><ExclamationIcon /> Policy Compliance Note</p>
                            <ul className="list-disc list-inside ml-4 mt-1">
                                {request.policyViolations.map((v, i) => <li key={i}>{v}</li>)}
                            </ul>
                        </div>
                    )}
                </div>
                <div className="flex justify-end mt-6">
                    <button onClick={onClose} className="px-6 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300">Close</button>
                </div>
            </div>
        </div>
    );
}

function RejectReasonModal({ onClose, onConfirm }) {
    const [reason, setReason] = useState('');
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
                <h2 className="text-xl font-bold text-red-600 mb-4">Reject Leave Request</h2>
                <p className="text-gray-600 mb-4">Please provide a reason for rejecting this request.</p>
                <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows="4" className="w-full p-2 border border-gray-300 rounded-md" placeholder="Rejection comments..."></textarea>
                <div className="flex justify-end space-x-4 mt-6">
                    <button onClick={onClose} className="px-6 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300">Cancel</button>
                    <button onClick={() => onConfirm(reason)} disabled={!reason} className="px-6 py-2 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 disabled:bg-red-300">Confirm Rejection</button>
                </div>
            </div>
        </div>
    );
}


// --- Main Component ---

export default function LeaveApprovalCenter({ userProfile, appId }) {
    const [activeTab, setActiveTab] = useState('pending');
    const [pendingRequests, setPendingRequests] = useState([]);
    const [pastRequests, setPastRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);

    useEffect(() => {
        const usersRef = collection(db, `/artifacts/${appId}/users`);
        const reportsQuery = query(usersRef, where("managerId", "==", userProfile.uid));
        
        const unsubUsers = onSnapshot(reportsQuery, (snapshot) => {
            const reportIds = snapshot.docs.map(doc => doc.id);

            if (reportIds.length > 0) {
                const requestsRef = collection(db, `/artifacts/${appId}/public/data/leaveRequests`);
                const requestsQuery = query(requestsRef, where("requesterId", "in", reportIds));

                const unsubRequests = onSnapshot(requestsQuery, (reqSnapshot) => {
                    const all = reqSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                    const pending = all.filter(r => ['Pending Manager Approval', 'Cancellation Requested'].includes(r.status));
                    const past = all.filter(r => !['Pending Manager Approval', 'Cancellation Requested'].includes(r.status));
                    
                    // Sort pending by request date
                    pending.sort((a, b) => b.createdAt.toDate() - a.createdAt.toDate());
                    // --- UPDATED: Sort past by action date (approvalDate) ---
                    past.sort((a, b) => (b.approvalDate || b.createdAt).toDate() - (a.approvalDate || a.createdAt).toDate());

                    setPendingRequests(pending);
                    setPastRequests(past);
                    setIsLoading(false);
                });
                return () => unsubRequests();
            } else {
                setPendingRequests([]);
                setPastRequests([]);
                setIsLoading(false);
            }
        });
        return () => unsubUsers();
    }, [appId, userProfile.uid]);

    const handleAction = async (request, newStatus, comments = "") => {
        const requestRef = doc(db, `/artifacts/${appId}/public/data/leaveRequests`, request.id);
        const requesterRef = doc(db, `/artifacts/${appId}/users`, request.requesterId);

        try {
            const requesterSnap = await getDoc(requesterRef);
            if (!requesterSnap.exists()) throw new Error("Requester profile not found.");
            
            await updateDoc(requestRef, { 
                status: newStatus, 
                approvalComments: comments,
                approverId: userProfile.uid,
                approverName: userProfile.name,
                approvalDate: serverTimestamp()
            });

            // Send notification
            const emailContent = leaveStatusUpdateTemplate(request, newStatus, comments);
            await sendNotificationEmail({ to: [requesterSnap.data().email], ...emailContent, appId });

            if (isRejectModalOpen) setIsRejectModalOpen(false);

        } catch (error) {
            console.error("Error processing leave action:", error);
        }
    };
    
    if (isLoading) return <LoadingSpinner />;
    
    return (
        <>
            {isModalOpen && <LeaveDetailModal request={selectedRequest} onClose={() => setIsModalOpen(false)} />}
            {isRejectModalOpen && <RejectReasonModal onClose={() => setIsRejectModalOpen(false)} onConfirm={(reason) => handleAction(selectedRequest, 'Rejected', reason)} />}
            
            <div className="bg-white p-8 rounded-lg shadow-md">
                <h2 className="text-2xl font-semibold text-[#213368] mb-4">Leave Approval Center</h2>
                <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8">
                        <button onClick={() => setActiveTab('pending')} className={`${activeTab === 'pending' ? 'border-[#213368] text-[#213368]' : 'border-transparent text-gray-500'} py-4 px-1 border-b-2 font-medium text-sm`}>
                            Pending Approvals ({pendingRequests.length})
                        </button>
                         <button onClick={() => setActiveTab('history')} className={`${activeTab === 'history' ? 'border-[#213368] text-[#213368]' : 'border-transparent text-gray-500'} py-4 px-1 border-b-2 font-medium text-sm`}>
                            Approval History ({pastRequests.length})
                        </button>
                    </nav>
                </div>
                {activeTab === 'pending' && (
                    <PendingRequestsTable 
                        requests={pendingRequests}
                        onView={(req) => { setSelectedRequest(req); setIsModalOpen(true); }}
                        onApprove={(req) => handleAction(req, req.status === 'Cancellation Requested' ? 'Cancelled' : 'Approved')}
                        onReject={(req) => { setSelectedRequest(req); setIsRejectModalOpen(true); }}
                    />
                )}
                {activeTab === 'history' && <PastRequestsTable requests={pastRequests} onView={(req) => { setSelectedRequest(req); setIsModalOpen(true); }} />}
            </div>
        </>
    );
}

function PendingRequestsTable({ requests, onView, onApprove, onReject }) {
    if (requests.length === 0) return <p className="text-center text-gray-500 py-8">No pending requests.</p>;
    return (
        <div className="overflow-x-auto mt-6">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requester</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Leave Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dates</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Applied On</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Violations</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">View</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 text-sm">
                    {requests.map(req => (
                        <tr key={req.id}>
                            <td className="px-6 py-4 whitespace-nowrap">{req.requesterName}</td>
                            <td className="px-6 py-4 whitespace-nowrap">{req.leaveType}</td>
                            <td className="px-6 py-4 whitespace-nowrap">{req.startDate.toDate().toLocaleDateString()} - {req.endDate.toDate().toLocaleDateString()}</td>
                            <td className="px-6 py-4 whitespace-nowrap">{req.createdAt.toDate().toLocaleDateString()}</td>
                            <td className="px-6 py-4 text-center">
                                {req.policyViolations?.length > 0 && <ExclamationIcon className="text-yellow-500 h-5 w-5" title={req.policyViolations.join(', ')} />}
                            </td>
                            <td className="px-6 py-4">
                                <button onClick={() => onView(req)} className="p-2 text-gray-500 hover:text-[#20BEC6]"><EyeIcon /></button>
                            </td>
                            <td className="px-6 py-4 space-x-3">
                                <button onClick={() => onApprove(req)} title="Approve" className="p-2 text-green-500 hover:text-green-700"><CheckCircleIcon /></button>
                                <button onClick={() => onReject(req)} title="Reject" className="p-2 text-red-500 hover:text-red-700"><XCircleIcon /></button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function PastRequestsTable({ requests, onView }) {
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    const paginatedRequests = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return requests.slice(startIndex, startIndex + itemsPerPage);
    }, [requests, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(requests.length / itemsPerPage);

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    if (requests.length === 0) return <p className="text-center text-gray-500 py-8">No past approvals.</p>;

    return (
        <div className="mt-6">
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requester</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Leave Type</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dates</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            {/* --- UPDATED: Column header changed --- */}
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">View</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 text-sm">
                        {paginatedRequests.map(req => (
                            <tr key={req.id}>
                               <td className="px-6 py-4 whitespace-nowrap">{req.requesterName}</td>
                                <td className="px-6 py-4 whitespace-nowrap">{req.leaveType}</td>
                                <td className="px-6 py-4 whitespace-nowrap">{req.startDate.toDate().toLocaleDateString()} - {req.endDate.toDate().toLocaleDateString()}</td>
                                <td className="px-6 py-4 whitespace-nowrap">{req.status}</td>
                                {/* --- UPDATED: Displaying the approvalDate --- */}
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {req.approvalDate ? req.approvalDate.toDate().toLocaleDateString() : 'N/A'}
                                </td>
                                 <td className="px-6 py-4">
                                    <button onClick={() => onView(req)} className="p-2 text-gray-500 hover:text-[#20BEC6]"><EyeIcon /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
             {totalPages > 1 && (
                <div className="flex justify-between items-center mt-4">
                    <div className="flex items-center space-x-2 text-sm">
                        <span>Items per page:</span>
                        <select value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))} className="border border-gray-300 rounded-md p-1">
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                    <div className="flex items-center space-x-2">
                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 border rounded-md disabled:opacity-50">Previous</button>
                        <span>Page {currentPage} of {totalPages}</span>
                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1 border rounded-md disabled:opacity-50">Next</button>
                    </div>
                </div>
            )}
        </div>
    );
}

